import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { SimpleAlert } from "../components/Alerts/Alerts";
import { startConfirmSeatsCheckout } from "../actions/featureAddons";
import {
  EXTRA_SEATS_CHECKOUT_CANCELLED,
  EXTRA_SEATS_CHECKOUT_FORBIDDEN,
  EXTRA_SEATS_CHECKOUT_INCOMPLETE,
} from "../i18n/keysTranslations";
import { useTranslationApp } from "../lib/i18next";
import { useDispatchApp } from "../lib/redux";
import {
  ALERT_ICON_TYPE_ERROR,
  ALERT_ICON_TYPE_INFO,
} from "../utils/constants";
import {
  SEATS_CHECKOUT_CANCEL,
  SEATS_CHECKOUT_CONFIRMATION,
  SEATS_CHECKOUT_QUERY,
  SEATS_CHECKOUT_SUCCESS,
  clearSeatDraftUser,
  interpretSeatsCheckoutConfirmation,
  stripSeatsCheckoutSearch,
} from "../utils/extraSeats";

const useSeatsCheckoutReturn = ({ enabled = true, onPaid } = {}) => {
  const dispatch = useDispatchApp();
  const { t } = useTranslationApp();
  const [params, setParams] = useSearchParams();
  const handledRef = useRef("");
  const mountedRef = useRef(true);
  const onPaidRef = useRef(onPaid);
  onPaidRef.current = onPaid;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    const flag = params.get(SEATS_CHECKOUT_QUERY);
    if (flag !== SEATS_CHECKOUT_SUCCESS && flag !== SEATS_CHECKOUT_CANCEL) {
      return undefined;
    }

    const sessionID = params.get("session_id") || "";
    const key = `${flag}:${sessionID}`;
    if (handledRef.current === key) return undefined;
    handledRef.current = key;

    const stripQuery = () => {
      const next = stripSeatsCheckoutSearch(window.location.search);
      setParams(next, { replace: true });
    };

    // A params change while confirming must not drop the result, so only an
    // unmount stops this run; handledRef already prevents a second run.
    const run = async () => {
      if (flag === SEATS_CHECKOUT_CANCEL) {
        clearSeatDraftUser();
        SimpleAlert({
          title: t(EXTRA_SEATS_CHECKOUT_CANCELLED),
          icon: ALERT_ICON_TYPE_INFO,
        });
        stripQuery();
        return;
      }

      if (!sessionID) {
        clearSeatDraftUser();
        SimpleAlert({
          title: t(EXTRA_SEATS_CHECKOUT_INCOMPLETE),
          icon: ALERT_ICON_TYPE_INFO,
        });
        stripQuery();
        return;
      }

      const result = await dispatch(startConfirmSeatsCheckout({ sessionID }));
      if (!mountedRef.current) return;

      const confirmation = interpretSeatsCheckoutConfirmation(result);
      if (confirmation === SEATS_CHECKOUT_CONFIRMATION.PAID) {
        await onPaidRef.current?.(result);
      } else {
        const forbidden =
          confirmation === SEATS_CHECKOUT_CONFIRMATION.FORBIDDEN;
        SimpleAlert({
          title: t(
            forbidden
              ? EXTRA_SEATS_CHECKOUT_FORBIDDEN
              : EXTRA_SEATS_CHECKOUT_INCOMPLETE
          ),
          icon: forbidden ? ALERT_ICON_TYPE_ERROR : ALERT_ICON_TYPE_INFO,
        });
      }

      clearSeatDraftUser();
      stripQuery();
    };

    run();
    return undefined;
  }, [dispatch, enabled, params, setParams, t]);
};

export default useSeatsCheckoutReturn;
